import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { XMarkIcon, MagnifyingGlassIcon, MusicalNoteIcon, PlayIcon, PauseIcon } from '@heroicons/react/24/outline';
import { IBgm, BgmCategory, BgmGenStatus, PaginationQuery, BgmQuery, BGM_TYPES, BgmCategoryLabels, IMusicGeneration, TaskStatus, getLabelsByLocale, Locale, getBgmTypeDesc } from '@packages/global';
import { bgmApi } from '../../services/user/bgm.api';
import { musicApi } from '../../services/user/music.api';
import Pagination from '../Pagination';

interface MusicSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (bgm: IBgm) => void;
  /** 是否显示系统音乐库 tab，默认 true */
  showLibrary?: boolean;
}

export default function MusicSelector({ isOpen, onClose, onSelect, showLibrary = true }: MusicSelectorProps) {
  const { t, i18n } = useTranslation(['common_ui', 'common']);
  const [activeTab, setActiveTab] = useState<'library' | 'myMusic'>(showLibrary ? 'library' : 'myMusic');
  const [bgms, setBgms] = useState<IBgm[]>([]);
  const [myMusic, setMyMusic] = useState<IMusicGeneration[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedBgm, setSelectedBgm] = useState<IBgm | null>(null);
  const [selectedMusic, setSelectedMusic] = useState<IMusicGeneration | null>(null);
  const [filterTypeId, setFilterTypeId] = useState<number | ''>('');
  const [filterCategory, setFilterCategory] = useState<BgmCategory | ''>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0, page: 1, limit: 24 });
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRefs = useRef<{ [key: string]: HTMLAudioElement }>({});

  const loadBgms = async (page: number = 1) => {
    setLoading(true);
    try {
      const queryData: PaginationQuery<BgmQuery> = {
        query: {
          search: search || undefined,
          bgmTypeId: filterTypeId || undefined,
          bgmCategory: filterCategory || undefined,
          genStatus: BgmGenStatus.GENERATING_COMPLETED
        },
        pagination: {
          page,
          limit: 15,
          sortBy: 'seq',
          sortOrder: 'asc'
        }
      };
      const response = await bgmApi.bgmPageList(queryData);
      setBgms(response.items);
      setPagination(response.pagination);
      setCurrentPage(page);
    } catch (error) {
      console.error('加载背景音乐失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMyMusic = async (page: number = 1) => {
    setLoading(true);
    try {
      const response = await musicApi.musicGenerationPageList({
        query: {
          search: search || undefined,
          status: TaskStatus.COMPLETED
        },
        pagination: {
          page,
          limit: 15,
          sortBy: 'createdAt',
          sortOrder: 'desc'
        }
      });
      setMyMusic(response.items);
      setPagination(response.pagination);
      setCurrentPage(page);
    } catch (error) {
      console.error('加载我的音乐失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setSelectedBgm(null);
      setSelectedMusic(null);
      setCurrentPage(1);
      if (activeTab === 'library') {
        loadBgms(1);
      } else {
        loadMyMusic(1);
      }
    } else {
      Object.values(audioRefs.current).forEach(audio => {
        audio.pause();
        audio.currentTime = 0;
      });
      setPlayingId(null);
    }
  }, [isOpen, activeTab, search, filterTypeId, filterCategory]);

  const handlePlayPause = (id: string, audioUrl: string) => {
    const audio = audioRefs.current[id];
    if (!audio) return;

    if (playingId === id) {
      audio.pause();
      setPlayingId(null);
    } else {
      Object.entries(audioRefs.current).forEach(([audioId, audioEl]) => {
        if (audioId !== id) {
          audioEl.pause();
          audioEl.currentTime = 0;
        }
      });
      audio.play();
      setPlayingId(id);
    }
  };

  const handleAudioEnded = (bgmId: string) => {
    if (playingId === bgmId) {
      setPlayingId(null);
    }
  };

  const handleSelect = () => {
    if (activeTab === 'library' && selectedBgm) {
      onSelect(selectedBgm);
      onClose();
    } else if (activeTab === 'myMusic' && selectedMusic) {
      const displayName = selectedMusic.title
        || (selectedMusic.prompt
          ? selectedMusic.prompt.substring(0, 50) + (selectedMusic.prompt.length > 50 ? '...' : '')
          : selectedMusic.id.substring(0, 8));
      const audioUrl = selectedMusic.resultAudioUrl || '';
      if (!audioUrl) {
        alert(t('common_ui:selector.music.noAudioFile'));
        return;
      }
      const bgmFromMusic: IBgm = {
        id: selectedMusic.id,
        name: displayName,
        audioUrl,
        duration: selectedMusic.duration,
        bgmTypeId: 0,
        bgmCategory: 'GENERATED' as BgmCategory,
        genStatus: BgmGenStatus.GENERATING_COMPLETED,
        seq: 0,
        createdAt: selectedMusic.createdAt,
        updatedAt: selectedMusic.updatedAt
      };
      onSelect(bgmFromMusic);
      onClose();
    }
  };

  const formatDuration = (duration: number) => {
    const minutes = Math.floor(duration / 60);
    const seconds = Math.floor(duration % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[90]">
      <div className="bg-bg-surface rounded-lg w-[900px] h-[610px] flex flex-col border border-border-subtle shadow-2xl">
        <div className="flex items-center p-4 border-b border-border-subtle">
          <div className="flex items-center gap-1">
            {showLibrary && (
              <button
                onClick={() => {
                  setActiveTab('library');
                  setSearch('');
                  setFilterTypeId('');
                  setFilterCategory('');
                }}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${activeTab === 'library'
                  ? 'text-blue-400 bg-blue-500/10'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-bg-elevated'
                  }`}
              >
                {t('common_ui:selector.music.tabLibrary')}
              </button>
            )}
            <button
              onClick={() => {
                setActiveTab('myMusic');
                setSearch('');
              }}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${activeTab === 'myMusic'
                ? 'text-blue-400 bg-blue-500/10'
                : 'text-gray-400 hover:text-gray-200 hover:bg-bg-elevated'
                }`}
            >
              {t('common_ui:selector.music.tabMyMusic')}
            </button>
          </div>
          <div className="flex items-center gap-3 ml-auto">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('common_ui:selector.music.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-60 pl-9 pr-4 py-1.5 border border-border-subtle rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-bg-form text-gray-50 placeholder-gray-500 text-sm"
              />
            </div>
            {activeTab === 'library' && (
              <>
                <select
                  value={filterTypeId}
                  onChange={(e) => setFilterTypeId(e.target.value ? Number(e.target.value) : '')}
                  className="px-3 py-1.5 border border-border-subtle rounded-lg bg-bg-form text-gray-50 text-sm"
                >
                  <option value="">{t('common_ui:selector.music.allTypes')}</option>
                  {BGM_TYPES.map(type => (
                    <option key={type.id} value={type.id}>{getBgmTypeDesc(type, i18n.language as Locale)}</option>
                  ))}
                </select>
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value as BgmCategory | '')}
                  className="px-3 py-1.5 border border-border-subtle rounded-lg bg-bg-form text-gray-50 text-sm"
                >
                  <option value="">{t('common_ui:selector.music.allCategories')}</option>
                  {Object.entries(getLabelsByLocale(BgmCategoryLabels, i18n.language as Locale)).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-200 cursor-pointer">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : activeTab === 'library' ? (
            bgms.length === 0 ? (
              <div className="text-center text-gray-400 py-12">
                <div className="text-6xl mb-4">🎵</div>
                <p>{t('common_ui:selector.music.empty')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                {bgms.map((bgm) => (
                  <div
                    key={bgm.id}
                    onClick={() => setSelectedBgm(bgm)}
                    className={`relative cursor-pointer rounded-lg p-3 border-2 transition-all ${selectedBgm?.id === bgm.id || playingId === bgm.id
                      ? 'border-blue-500 ring-2 ring-blue-400 bg-blue-50/5'
                      : 'border-border-subtle hover:border-border-default bg-bg-elevated/50'
                      }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="relative flex-shrink-0">
                        {bgm.coverImageUrl ? (
                          <img
                            src={bgm.coverImageUrl}
                            alt={bgm.name}
                            className="w-12 h-12 object-cover rounded-full"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-bg-elevated rounded-full flex items-center justify-center">
                            <MusicalNoteIcon className="h-6 w-6 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-gray-50 truncate" title={bgm.name}>
                          {bgm.name}
                        </h3>
                        <div className="flex items-center gap-2 text-xs text-gray-400 mt-1">
                          <span>{bgm.bgmType?.desc || t('common_ui:selector.music.unknownType')}</span>
                          <span>•</span>
                          <span>{formatDuration(bgm.duration)}</span>
                        </div>
                      </div>
                      {bgm.audioUrl && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePlayPause(bgm.id, bgm.audioUrl!);
                            setSelectedBgm(bgm);
                          }}
                          className={`flex-shrink-0 rounded-full w-8 h-8 flex items-center justify-center transition-colors cursor-pointer ${playingId === bgm.id
                            ? 'bg-blue-500 hover:bg-blue-600 text-white'
                            : 'bg-bg-elevated hover:bg-border-default text-gray-300'
                            }`}
                        >
                          {playingId === bgm.id ? (
                            <PauseIcon className="h-4 w-4" />
                          ) : (
                            <PlayIcon className="h-4 w-4" />
                          )}
                        </button>
                      )}
                    </div>
                    {bgm.audioUrl && (
                      <audio
                        ref={(el) => {
                          if (el) audioRefs.current[bgm.id] = el;
                        }}
                        preload="metadata"
                        onEnded={() => handleAudioEnded(bgm.id)}
                      >
                        <source src={bgm.audioUrl} type="audio/mpeg" />
                        <source src={bgm.audioUrl} type="audio/wav" />
                      </audio>
                    )}
                  </div>
                ))}
              </div>
            )
          ) : (
            myMusic.length === 0 ? (
              <div className="text-center text-gray-400 py-12">
                <div className="text-6xl mb-4">🎵</div>
                <p>{t('common_ui:selector.music.emptyMyMusic')}</p>
                <p className="text-sm mt-2">{t('common_ui:selector.music.emptyMyMusicHint')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                {myMusic.map((music) => (
                  <div
                    key={music.id}
                    onClick={() => setSelectedMusic(music)}
                    className={`relative cursor-pointer rounded-lg p-3 border-2 transition-all ${selectedMusic?.id === music.id || playingId === music.id
                      ? 'border-blue-500 ring-2 ring-blue-400 bg-blue-50/5'
                      : 'border-border-subtle hover:border-border-default bg-bg-elevated/50'
                      }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="relative flex-shrink-0">
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                          <MusicalNoteIcon className="h-6 w-6 text-white" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-gray-50 truncate" title={music.title || music.prompt}>
                          {music.title || (music.prompt.substring(0, 30) + (music.prompt.length > 30 ? '...' : ''))}
                        </h3>
                        <div className="flex items-center gap-2 text-xs text-gray-400 mt-1">
                          <span>{formatDuration(music.duration)}</span>
                          <span>•</span>
                          <span>{new Date(music.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                      {music.resultAudioUrl && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePlayPause(music.id, music.resultAudioUrl!);
                            setSelectedMusic(music);
                          }}
                          className={`flex-shrink-0 rounded-full w-8 h-8 flex items-center justify-center transition-colors cursor-pointer ${playingId === music.id
                            ? 'bg-blue-500 hover:bg-blue-600 text-white'
                            : 'bg-bg-elevated hover:bg-border-default text-gray-300'
                            }`}
                        >
                          {playingId === music.id ? (
                            <PauseIcon className="h-4 w-4" />
                          ) : (
                            <PlayIcon className="h-4 w-4" />
                          )}
                        </button>
                      )}
                    </div>
                    {music.resultAudioUrl && (
                      <audio
                        ref={(el) => {
                          if (el) audioRefs.current[music.id] = el;
                        }}
                        preload="metadata"
                        onEnded={() => handleAudioEnded(music.id)}
                      >
                        <source src={music.resultAudioUrl} type="audio/mpeg" />
                        <source src={music.resultAudioUrl} type="audio/wav" />
                      </audio>
                    )}
                  </div>
                ))}
              </div>
            )
          )}
        </div>

        <div className="flex items-center justify-between p-4 border-t border-border-subtle bg-bg-elevated">
          <div className="flex-1">
            <Pagination
              currentPage={currentPage}
              totalPages={pagination.totalPages}
              total={pagination.total}
              limit={pagination.limit}
              onPageChange={activeTab === 'library' ? loadBgms : loadMyMusic}
              showInfo={false}
            />
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-300 bg-bg-surface border border-border-subtle rounded-lg hover:bg-bg-elevated cursor-pointer transition-colors"
            >
              {t('common:cancel')}
            </button>
            <button
              onClick={handleSelect}
              disabled={!selectedBgm && !selectedMusic}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
            >
              {t('common_ui:selector.music.confirm')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
